export const DEFAULT_CATALOG_THEME = Object.freeze({
  cor_primaria: "#11675D",
  cor_secundaria: "#F2B84B",
  cor_texto: "#17251F",
  cor_fundo: "#F7F5EF",
  estilo_card: "elevado",
  estilo_botao: "pill",
});

export const CATALOG_IMAGE_LIMITS = Object.freeze({
  max_bytes: 5 * 1024 * 1024,
  max_width: 5000,
  max_height: 5000,
  max_pixels: 20000000,
});

export const CATALOG_THEME_PRESETS = Object.freeze([
  {
    id: "vitrine",
    nome: "Vitrine",
    descricao: "Leve, acolhedor e focado nos produtos",
    tema: { ...DEFAULT_CATALOG_THEME },
  },
  {
    id: "aurora",
    nome: "Aurora",
    descricao: "Vibrante e contemporâneo",
    tema: {
      cor_primaria: "#6D4CFF",
      cor_secundaria: "#FF8A5B",
      cor_texto: "#172033",
      cor_fundo: "#F7F8FC",
      estilo_card: "suave",
      estilo_botao: "arredondado",
    },
  },
  {
    id: "botanico",
    nome: "Botânico",
    descricao: "Natural e acolhedor",
    tema: {
      cor_primaria: "#216E5A",
      cor_secundaria: "#F0B86E",
      cor_texto: "#18342D",
      cor_fundo: "#F4F8F5",
      estilo_card: "suave",
      estilo_botao: "arredondado",
    },
  },
  {
    id: "editorial",
    nome: "Editorial",
    descricao: "Sóbrio e sofisticado",
    tema: {
      cor_primaria: "#191919",
      cor_secundaria: "#C8A96B",
      cor_texto: "#202020",
      cor_fundo: "#F7F4EE",
      estilo_card: "linha",
      estilo_botao: "reto",
    },
  },
]);

export function normalizeBoolean(value, fallback = false) {
  if (value === true || value === 1 || value === "1" || value === "true") {
    return true;
  }

  if (value === false || value === 0 || value === "0" || value === "false") {
    return false;
  }

  return fallback;
}

export function slugifyCatalogName(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export function isValidHexColor(value) {
  return /^#[0-9a-f]{6}$/i.test(String(value || ""));
}

export function normalizeDecimal(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value.toFixed(2) : "";
  }

  const raw = String(value == null ? "" : value)
    .trim()
    .replace(/\s/g, "")
    .replace(/^R\$/i, "");

  if (!raw || !/^[+-]?\d[\d.,]*$/.test(raw)) return "";

  const sign = /^[+-]/.test(raw) ? raw.charAt(0) : "";
  const unsigned = sign ? raw.slice(1) : raw;
  let normalized = unsigned;

  if (unsigned.includes(",")) {
    const parts = unsigned.split(",");
    if (
      parts.length !== 2 ||
      !parts[1] ||
      parts[1].includes(".") ||
      !/^\d+(?:\.\d{3})*$/.test(parts[0]) ||
      !/^\d+$/.test(parts[1])
    ) {
      return "";
    }
    normalized = `${parts[0].replace(/\./g, "")}.${parts[1]}`;
  } else if (/^\d{1,3}(?:\.\d{3})+$/.test(unsigned)) {
    normalized = unsigned.replace(/\./g, "");
  } else if (!/^\d+(?:\.\d+)?$/.test(unsigned)) {
    return "";
  }

  normalized = `${sign}${normalized}`;
  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed.toFixed(2) : "";
}

export function sanitizeCatalogPriceInput(value) {
  const raw = String(value == null ? "" : value).trim();
  const negative = raw.includes("-");
  const unsigned = raw.replace(/[^0-9.,]/g, "");
  return `${negative ? "-" : ""}${unsigned}`;
}

export function validateCatalogProductPrice(value, maximum = 999999999.99) {
  const normalized = normalizeDecimal(value);
  let error = "";

  if (!normalized) {
    error = "Informe um preço válido.";
  } else if (Number(normalized) < 0) {
    error = "O preço não pode ser negativo.";
  } else if (Number(normalized) > maximum) {
    error = "Preço acima do limite permitido.";
  }

  return { normalized: error ? "" : normalized, error };
}

export function catalogProductPriceChanged(draft = {}) {
  return normalizeDecimal(draft.preco_input) !== draft.original_preco;
}

export function shouldRemoveCatalogMainImage(originalImage) {
  return Boolean(String(originalImage || "").trim());
}

function hasOwn(object, property) {
  return Object.prototype.hasOwnProperty.call(object || {}, property);
}

function hasExplicitCatalogMainImage(product = {}) {
  return hasOwn(product, "imagem") || hasOwn(product, "imagem_url");
}

function explicitCatalogMainImage(product = {}) {
  return String(product.imagem || product.imagem_url || "").trim();
}

export function validateCatalogImageDimensions(
  width,
  height,
  limits = CATALOG_IMAGE_LIMITS
) {
  const normalizedWidth = Number(width);
  const normalizedHeight = Number(height);

  if (
    !Number.isFinite(normalizedWidth) ||
    !Number.isFinite(normalizedHeight) ||
    normalizedWidth <= 0 ||
    normalizedHeight <= 0
  ) {
    return {
      valid: false,
      error: "Não foi possível ler as dimensões da imagem.",
    };
  }

  if (
    normalizedWidth > limits.max_width ||
    normalizedHeight > limits.max_height ||
    normalizedWidth * normalizedHeight > limits.max_pixels
  ) {
    return {
      valid: false,
      error: "A imagem deve ter no máximo 5000 × 5000 px e 20 megapixels.",
    };
  }

  return { valid: true, error: "" };
}

export function normalizeCatalogComplementaryImages(product = {}) {
  const hasExplicitComplementaryImages = hasOwn(
    product,
    "imagens_complementares"
  );
  const images = Array.isArray(product.imagens_complementares)
    ? product.imagens_complementares
    : [];
  const normalized = [];
  const occupiedSlots = new Set();

  images
    .slice()
    .sort((left, right) => Number(left.ordem || 0) - Number(right.ordem || 0))
    .forEach((image) => {
      const slot = Number(image && image.ordem);
      const url = String(
        (image && (image.imagem_url || image.url)) || ""
      ).trim();

      if ((slot !== 1 && slot !== 2) || !url || occupiedSlots.has(slot)) {
        return;
      }

      occupiedSlots.add(slot);
      normalized.push({ ordem: slot, imagem_url: url });
    });

  const imageUrls = Array.isArray(product.imagem_urls)
    ? product.imagem_urls.map((url) => String(url || "").trim()).filter(Boolean)
    : [];
  if (!normalized.length && !hasExplicitComplementaryImages) {
    const hasExplicitMain = hasExplicitCatalogMainImage(product);
    const explicitMain = explicitCatalogMainImage(product);
    const complementaryUrls = hasExplicitMain
      ? imageUrls.filter((url) => !explicitMain || url !== explicitMain)
      : imageUrls.slice(1);

    complementaryUrls.slice(0, 2).forEach((url, index) => {
      const slot = index + 1;
      occupiedSlots.add(slot);
      normalized.push({ ordem: slot, imagem_url: url });
    });
  }

  return normalized.sort((left, right) => left.ordem - right.ordem);
}

export function normalizeCatalogComplementaryRemovalSlots(value = []) {
  return Array.from(
    new Set(
      (Array.isArray(value) ? value : [])
        .map(Number)
        .filter((slot) => slot === 1 || slot === 2)
    )
  ).sort((left, right) => left - right);
}

export function buildCatalogProductPatch(draft = {}) {
  const item = { id: Number(draft.id) };

  if (
    Boolean(draft.catalogo_digital) !== Boolean(draft.original_catalogo_digital)
  ) {
    item.catalogo_digital = Boolean(draft.catalogo_digital);
  }

  if (draft.remover_imagem) item.remover_imagem = true;
  const complementaryRemovalSlots = normalizeCatalogComplementaryRemovalSlots(
    draft.remover_imagens_complementares
  ).filter((slot) => {
    const imageState =
      draft.complementary_images && draft.complementary_images[String(slot)];
    return !(imageState && imageState.image_file);
  });
  if (complementaryRemovalSlots.length) {
    item.remover_imagens_complementares = complementaryRemovalSlots;
  }
  if (catalogProductPriceChanged(draft)) {
    item.preco = normalizeDecimal(draft.preco_input);
  }

  return item;
}

function catalogConfigurationDraftValue(configuration = {}) {
  const normalized = normalizeCatalogConfiguration(configuration);
  return {
    ativo: normalized.ativo,
    nome: normalized.nome,
    slug: normalized.slug,
    descricao: normalized.descricao,
    logo_url: normalized.logo_url,
    capa_url: normalized.capa_url,
    tema: normalized.tema,
  };
}

export function isCatalogConfigurationDraftDirty(
  configuration,
  baseline,
  hasPendingAssets = false
) {
  if (hasPendingAssets) return true;
  return (
    JSON.stringify(catalogConfigurationDraftValue(configuration)) !==
    JSON.stringify(catalogConfigurationDraftValue(baseline))
  );
}

export function unpackCatalogEnvelope(payload) {
  if (
    payload &&
    typeof payload === "object" &&
    Object.prototype.hasOwnProperty.call(payload, "success")
  ) {
    if (!normalizeBoolean(payload.success)) {
      const error = new Error(
        payload.message || "Não foi possível concluir a operação."
      );
      error.errors = payload.errors || {};
      throw error;
    }

    return payload.data;
  }

  return payload;
}

export function extractCatalogCollection(payload, key) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  if (Array.isArray(payload[key])) return payload[key];
  if (Array.isArray(payload.data)) return payload.data;

  return [];
}

function parseTheme(value) {
  if (!value) return {};
  if (typeof value === "object") return value;

  try {
    return JSON.parse(value);
  } catch (error) {
    return {};
  }
}

export function normalizeCatalogConfiguration(payload = {}) {
  const source = payload.configuracao || payload || {};
  const theme = parseTheme(source.tema);

  return {
    id: source.id || null,
    ativo: normalizeBoolean(source.ativo),
    nome: source.nome || "",
    slug: source.slug || "",
    descricao: source.descricao || "",
    logo_url: source.logo_url || "",
    capa_url: source.capa_url || source.banner_url || "",
    public_url: source.public_url || source.url_publica || "",
    published_at: source.published_at || null,
    updated_at: source.updated_at || null,
    tema: {
      cor_primaria:
        theme.cor_primaria ||
        source.cor_primaria ||
        DEFAULT_CATALOG_THEME.cor_primaria,
      cor_secundaria:
        theme.cor_secundaria ||
        source.cor_secundaria ||
        DEFAULT_CATALOG_THEME.cor_secundaria,
      cor_texto: theme.cor_texto || DEFAULT_CATALOG_THEME.cor_texto,
      cor_fundo: theme.cor_fundo || DEFAULT_CATALOG_THEME.cor_fundo,
      estilo_card: theme.estilo_card || DEFAULT_CATALOG_THEME.estilo_card,
      estilo_botao: theme.estilo_botao || DEFAULT_CATALOG_THEME.estilo_botao,
    },
  };
}

export function normalizeCatalogProduct(product = {}) {
  const complementaryImages = normalizeCatalogComplementaryImages(product);
  const responseImageUrls = Array.isArray(product.imagem_urls)
    ? product.imagem_urls.map((url) => String(url || "").trim()).filter(Boolean)
    : [];
  const mainImage = hasExplicitCatalogMainImage(product)
    ? explicitCatalogMainImage(product)
    : responseImageUrls[0] || "";
  const imageUrls = [
    mainImage,
    ...complementaryImages.map((image) => image.imagem_url),
  ].filter((url, index, values) => url && values.indexOf(url) === index);

  return {
    ...product,
    id: product.id || product.id_produto,
    descricao: product.descricao || product.nome || "Produto sem nome",
    grupo: product.grupo || product.nome_grupo || "Sem grupo",
    valor_venda:
      product.valor_venda == null ? product.preco || 0 : product.valor_venda,
    imagem: mainImage,
    imagem_urls: imageUrls.length ? imageUrls : responseImageUrls,
    imagens_complementares: complementaryImages,
    catalogo_digital: normalizeBoolean(product.catalogo_digital),
    catalogo_elegivel: normalizeBoolean(product.catalogo_elegivel, true),
    motivo_inelegivel: product.motivo_inelegivel || "",
  };
}

export function normalizeCatalogPagination(payload = {}, fallback = {}) {
  const source = payload.meta || payload.pagination || payload || {};
  const currentPage = Math.max(
    1,
    Number(source.current_page || source.page || fallback.current_page || 1)
  );
  const lastPage = Math.max(
    1,
    Number(source.last_page || source.pages || fallback.last_page || 1)
  );

  return {
    current_page: Math.min(currentPage, lastPage),
    last_page: lastPage,
    per_page: Math.max(1, Number(source.per_page || fallback.per_page || 12)),
    total: Math.max(0, Number(source.total || 0)),
    from: source.from == null ? null : Number(source.from),
    to: source.to == null ? null : Number(source.to),
  };
}

export function normalizeCatalogPromotion(promotion = {}) {
  return {
    ...promotion,
    id: promotion.id || null,
    nome: promotion.nome || "Promoção",
    descricao: promotion.descricao || "",
    tipo: promotion.tipo || "percentual",
    valor: normalizeDecimal(promotion.valor) || "0.00",
    escopo: promotion.escopo || "produto",
    id_produto: promotion.id_produto || promotion.produto_id || null,
    inicio_em: promotion.inicio_em || null,
    fim_em: promotion.fim_em || null,
    ativo: normalizeBoolean(promotion.ativo, true),
    destaque: normalizeBoolean(promotion.destaque),
    ordem: Number(promotion.ordem || 0),
  };
}

export function promotionStatus(promotion, now = new Date()) {
  if (!normalizeBoolean(promotion.ativo, true)) return "inativa";

  const currentTime = now.getTime();
  const start = promotion.inicio_em
    ? new Date(promotion.inicio_em).getTime()
    : null;
  const end = promotion.fim_em ? new Date(promotion.fim_em).getTime() : null;

  if (start && currentTime < start) return "agendada";
  if (end && currentTime > end) return "encerrada";
  return "ativa";
}
